import { IActivityLocation } from '@curbnturf/entities';
import { DUMP_STATION_ICON } from '../icons/custom-icons';

/**
 * The 24 category-prefixed URL slugs an activity-location (POI) detail page
 * can live under, matching `serverless/sitemap/sitemap-generator.js`'s
 * hardcoded prefix strings exactly (load-bearing for SEO parity — a typo
 * here silently breaks the sitemap↔route contract). `wind-sports` is a
 * deliberate addition beyond the legacy `curbnturf` app's 23 mounted
 * category routes: the sitemap generator already emits `/wind-sports/...`
 * URLs for POIs whose only flag is `activityWindSports`, but legacy's own
 * client never mounted that parent route, so those URLs 404 even in
 * production today. `activity` is the generic/fallback prefix for a POI
 * matching none of the other 23 category flags (only `customActivity` set).
 */
export type ActivityCategorySlug =
  | 'activity'
  | 'rv-dump-site'
  | 'rest-area'
  | 'wifi'
  | 'fresh-water'
  | 'propane'
  | 'biking'
  | 'boating'
  | 'fishing'
  | 'golf'
  | 'hiking'
  | 'horseback-riding'
  | 'hunting'
  | 'kayaking'
  | 'motor-sports'
  | 'ohv-trails'
  | 'rafting'
  | 'rock-climbing'
  | 'snow-sports'
  | 'surfing'
  | 'swimming'
  | 'water-sports'
  | 'wildlife-watching'
  | 'wind-sports';

export interface IActivityCategoryMeta {
  slug: ActivityCategorySlug;
  label: string;
  /** The `IActivityLocation` boolean field this category corresponds to.
   *  Undefined only for the generic `activity` fallback slug. */
  field?: keyof IActivityLocation;
  icon: string; // Material Symbols name
  /** JSON-LD hint for the view page's structured data. Utility-stop
   *  categories aren't really "attractions," so they get a plainer type. */
  schemaType: 'TouristAttraction' | 'CivicStructure';
}

export const ACTIVITY_CATEGORY_META: Record<ActivityCategorySlug, IActivityCategoryMeta> = {
  'rv-dump-site': { slug: 'rv-dump-site', label: 'RV Dump Stations', field: 'categoryDump', icon: DUMP_STATION_ICON, schemaType: 'CivicStructure' },
  'rest-area': { slug: 'rest-area', label: 'Rest Areas', field: 'categoryRestArea', icon: 'local_parking', schemaType: 'CivicStructure' },
  wifi: { slug: 'wifi', label: 'WiFi Spots', field: 'categoryWifi', icon: 'wifi', schemaType: 'CivicStructure' },
  'fresh-water': { slug: 'fresh-water', label: 'Fresh Water', field: 'categoryPotableWater', icon: 'water_drop', schemaType: 'CivicStructure' },
  propane: { slug: 'propane', label: 'Propane Stations', field: 'categoryPropane', icon: 'propane_tank', schemaType: 'CivicStructure' },
  biking: { slug: 'biking', label: 'Biking Trails', field: 'activityBiking', icon: 'directions_bike', schemaType: 'TouristAttraction' },
  boating: { slug: 'boating', label: 'Boating', field: 'activityBoating', icon: 'directions_boat', schemaType: 'TouristAttraction' },
  fishing: { slug: 'fishing', label: 'Fishing Spots', field: 'activityFishing', icon: 'phishing', schemaType: 'TouristAttraction' },
  golf: { slug: 'golf', label: 'Golf Courses', field: 'activityGolf', icon: 'golf_course', schemaType: 'TouristAttraction' },
  hiking: { slug: 'hiking', label: 'Hiking Trails', field: 'activityHiking', icon: 'hiking', schemaType: 'TouristAttraction' },
  'horseback-riding': { slug: 'horseback-riding', label: 'Horseback Riding', field: 'activityHorseback', icon: 'nature_people', schemaType: 'TouristAttraction' },
  hunting: { slug: 'hunting', label: 'Hunting Grounds', field: 'activityHunting', icon: 'target', schemaType: 'TouristAttraction' },
  kayaking: { slug: 'kayaking', label: 'Kayaking', field: 'activityKayaking', icon: 'kayaking', schemaType: 'TouristAttraction' },
  'motor-sports': { slug: 'motor-sports', label: 'Motor Sports', field: 'activityMotorSports', icon: 'sports_motorsports', schemaType: 'TouristAttraction' },
  'ohv-trails': { slug: 'ohv-trails', label: 'OHV Trails', field: 'activityOhv', icon: 'agriculture', schemaType: 'TouristAttraction' },
  rafting: { slug: 'rafting', label: 'Rafting', field: 'activityRafting', icon: 'kayaking', schemaType: 'TouristAttraction' },
  'rock-climbing': { slug: 'rock-climbing', label: 'Rock Climbing', field: 'activityRockClimbing', icon: 'landscape', schemaType: 'TouristAttraction' },
  'snow-sports': { slug: 'snow-sports', label: 'Snow Sports', field: 'activitySnowSports', icon: 'downhill_skiing', schemaType: 'TouristAttraction' },
  surfing: { slug: 'surfing', label: 'Surfing', field: 'activitySurfing', icon: 'surfing', schemaType: 'TouristAttraction' },
  swimming: { slug: 'swimming', label: 'Swimming', field: 'activitySwimming', icon: 'pool', schemaType: 'TouristAttraction' },
  'water-sports': { slug: 'water-sports', label: 'Water Sports', field: 'activityWaterSports', icon: 'water', schemaType: 'TouristAttraction' },
  'wildlife-watching': { slug: 'wildlife-watching', label: 'Wildlife Watching', field: 'activityWildlifeWatching', icon: 'pets', schemaType: 'TouristAttraction' },
  'wind-sports': { slug: 'wind-sports', label: 'Wind Sports', field: 'activityWindSports', icon: 'air', schemaType: 'TouristAttraction' },
  activity: { slug: 'activity', label: 'Points of Interest', icon: 'place', schemaType: 'TouristAttraction' },
};

export const ALL_ACTIVITY_CATEGORY_SLUGS: ActivityCategorySlug[] = [
  'rv-dump-site', 'rest-area', 'wifi', 'fresh-water', 'propane',
  'biking', 'boating', 'fishing', 'golf', 'hiking', 'horseback-riding', 'hunting',
  'kayaking', 'motor-sports', 'ohv-trails', 'rafting', 'rock-climbing', 'snow-sports',
  'surfing', 'swimming', 'water-sports', 'wildlife-watching', 'wind-sports',
  'activity',
];

/** Best-guess category slug for a POI whose route data didn't carry one
 *  (e.g. a deep link with no known prefix) — first matching boolean flag,
 *  falling back to the generic `activity` slug. */
export function inferActivityCategorySlug(activity: IActivityLocation): ActivityCategorySlug {
  for (const slug of ALL_ACTIVITY_CATEGORY_SLUGS) {
    const field = ACTIVITY_CATEGORY_META[slug].field;
    if (field && activity[field]) return slug;
  }
  return 'activity';
}
